// eslint-disable no-console

import { ConnectIdType, ConnectMessageType } from '@idConnect/types';
import { LogLevelType } from '@shared/content/types';
import { Ctx } from './core';

export function validate<T, Fallback = null>(
  value: unknown,
  validator: (val: unknown) => val is T,
  defaultValue: Fallback,
): T | Fallback {
  if (validator(value)) {
    return value;
  } else {
    return defaultValue;
  }
}

export type Validator<T> = ReturnType<typeof makeValidator<T, T>>;
export function makeValidator<T, Fallback = null>(
  validator: (val: unknown) => val is T,
  defaultValue: Fallback,
) {
  return function value(value: unknown): T | Fallback {
    if (validator(value)) {
      return value;
    } else {
      return defaultValue;
    }
  };
}

export function isNumber(x: unknown): x is number {
  return typeof x === 'number';
}

export type UtiqEligibility = {
  isEligible: boolean;
  retryEligibilityCheckTime: number | null;
  eligibilityCheckTime: number;
};
export function isUtiqEligibility(x: unknown): x is UtiqEligibility {
  return (
    typeof x === 'object' &&
    x !== null &&
    'eligibilityCheckTime' in x &&
    typeof x.eligibilityCheckTime === 'number' &&
    'retryEligibilityCheckTime' in x &&
    'isEligible' in x &&
    typeof x.isEligible === 'boolean'
  );
}

export function isLogLevelType(x: unknown): x is LogLevelType {
  return (
    x === 'silent' ||
    x === 'error' ||
    x === 'warn' ||
    x === 'info' ||
    x === 'verbose'
  );
}

export type UtiqPass = { connectId: ConnectIdType };
export function isUtiqPass(x: unknown): x is UtiqPass {
  return (
    typeof x === 'object' &&
    x !== null &&
    'connectId' in x &&
    typeof x.connectId === 'object' &&
    x.connectId !== null &&
    'domain' in x.connectId &&
    'idGraph' in x.connectId &&
    'value' in x.connectId &&
    'ttl' in x.connectId
  );
}

export async function waitFor<T>(fn: () => T, timeout = 1000): Promise<T> {
  return new Promise((done, fail) => {
    function check(interval: number) {
      const pass = (() => {
        try {
          return fn();
        } catch {
          return false;
        }
      })();

      if (pass) {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        done(pass as any);
      } else {
        setTimeout(check, interval, interval * (1 + Math.random()));
      }
    }

    setTimeout(check, 0, 1);
    setTimeout(fail, timeout);
  });
}

export type ValueOf<T> = T[keyof T];

export function unwrap<T>(x: T | null | undefined): T {
  if (x === null) {
    console.error(x);
    throw new Error('unwrap');
  }

  if (x === undefined) {
    console.error(x);
    throw new Error('unwrap');
  }

  return x;
}

export function isConnectMessageType(msg: unknown): msg is ConnectMessageType {
  return (
    typeof msg === 'object' &&
    msg != null &&
    'msgType' in msg &&
    typeof msg.msgType === 'string'
  );
}

type TryOriginSources = 'idConnectConfig' | 'customUtiqHost' | 'utiqHost';

type UrlFactory = {
  tryPathFrom: (url: string | null | undefined) => UrlFactory | null;
  addPathSegment: (seg: string) => UrlFactory;
  withTransactionId: () => UrlFactory;
  withParam: (key: string, val: string | null | undefined) => UrlFactory;
  render: () => string;
};

export function buildUrl(
  ctx: Ctx,
  tryOriginSources: TryOriginSources[],
): UrlFactory | null {
  let urlMaybe = null as URL | null;

  for (const originSource of tryOriginSources) {
    ({
      idConnectConfig: () => {
        if (
          ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
            .IDCONNECT_API_HOST
        )
          urlMaybe = new URL(
            ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG.IDCONNECT_API_HOST,
          );
      },

      customUtiqHost: () => {
        if (ctx.window.Utiq?.config?.customUtiqHost)
          urlMaybe = new URL(ctx.window.Utiq?.config?.customUtiqHost);
      },

      utiqHost: () => {
        if (window.Utiq?.config?.utiqHost)
          urlMaybe = new URL(window.Utiq?.config?.utiqHost);
      },
    })[originSource]();
  }

  if (urlMaybe === null) return null;

  const url = urlMaybe;

  const self = {
    tryPathFrom: (input: string | null | undefined) => {
      if (input) {
        const { pathname } = new URL(input);
        url.pathname = pathname;
        return self;
      }

      return null;
    },

    addPathSegment: (seg: string) => {
      url.pathname = [...url.pathname.split('/'), seg].join('/');

      return self;
    },

    withParam: (key: string, val: string | null | undefined) => {
      if (val) url.searchParams.set(key, val);

      return self;
    },

    withTransactionId: () => {
      if (ctx?.window?.Utiq?.transactionId)
        url.searchParams.set('transactionId', ctx?.window?.Utiq?.transactionId);
      return self;
    },

    render: () => url.toString(),
  };

  return self;
}
